// Region anchoring for the browse/search pages.
//
// The homepage "This week's favourites" / category carousel is geo-aware: it
// shows classics near the visitor. Its "Browse all" + category-tab links used
// to navigate to a bare `/search`, which the search pages relevance-rank
// globally → a US-first dump for, say, a UK visitor. To keep continuity we:
//   1. carry `?country=<CC>` on those homepage links, and
//   2. default the search pages to the detected country when there's no
//      explicit `?location=` / map viewport.
//
// This module is the single source of truth for resolving that anchor so the
// Algolia and Supabase search paths stay in lockstep.

const COUNTRY_CODE_RE = /^[A-Z]{2}$/;

// Sentinel the "Search everywhere" affordance writes to `?country=` to opt out
// of region anchoring (explicit user choice to widen to global relevance).
export const COUNTRY_PARAM_WIDEN = "all";

/**
 * Normalise a raw `?country=` URL value.
 * - a valid ISO-3166 alpha-2 → uppercased code (e.g. "gb" → "GB")
 * - the widen sentinel ("all", any case) → "all"
 * - anything else (empty, junk, 3+ chars) → null (treated as "not provided")
 */
export function normalizeCountryParam(
  raw: string | null | undefined,
): string | typeof COUNTRY_PARAM_WIDEN | null {
  if (!raw) return null;
  const v = raw.trim();
  if (!v) return null;
  if (v.toLowerCase() === COUNTRY_PARAM_WIDEN) return COUNTRY_PARAM_WIDEN;
  const up = v.toUpperCase();
  return COUNTRY_CODE_RE.test(up) ? up : null;
}

export interface BrowseCountryAnchorInput {
  /** Normalised `?country=` value (use normalizeCountryParam). */
  countryParam: string | typeof COUNTRY_PARAM_WIDEN | null;
  /** Visitor's detected country (locale → IP hint → browser), ISO-2 or null. */
  visitorCountry?: string | null;
  /** A text/geocoded location is in play (URL `?location=` or typed). */
  hasLocation: boolean;
  /** A map viewport / bounding box is active. */
  hasBounds: boolean;
  /**
   * The country we'd anchor to is hidden from search (e.g. US under
   * `feature_us_hide_from_search`). Anchoring to it would yield 0 results, so
   * we fall back to global (which already excludes the hidden country).
   */
  banUs?: boolean;
}

export interface BrowseCountryAnchor {
  /** ISO-2 country to constrain results to, or null when not anchoring. */
  anchorCountry: string | null;
  /** Whether a region anchor is being applied (drives the affordance + filter). */
  applied: boolean;
}

/**
 * Resolve whether — and to which country — the browse page should anchor.
 *
 * Precedence: an explicit location / map viewport always wins (no anchor); an
 * explicit "all" widen opts out; otherwise we anchor to the explicit
 * `?country=` code, falling back to the visitor's detected country.
 */
export function resolveBrowseCountryAnchor(
  input: BrowseCountryAnchorInput,
): BrowseCountryAnchor {
  const { countryParam, visitorCountry, hasLocation, hasBounds, banUs } = input;

  const none: BrowseCountryAnchor = { anchorCountry: null, applied: false };

  // Explicit location or map viewport is the source of truth.
  if (hasLocation || hasBounds) return none;
  // Explicit "Search everywhere".
  if (countryParam === COUNTRY_PARAM_WIDEN) return none;

  const cc =
    countryParam ||
    (visitorCountry ? visitorCountry.trim().toUpperCase() : "");
  if (!cc || !COUNTRY_CODE_RE.test(cc)) return none;

  // Don't pin to a country we hide from search.
  if (banUs && cc === "US") return none;

  return { anchorCountry: cc, applied: true };
}

/** Search filters applied after a user clicks a clean search link. */
export function buildBrowseSearchState(opts: {
  countryCode?: string | null;
  carType?: string | null;
}): Record<string, string> {
  const state: Record<string, string> = {};
  if (opts.carType) state.car_type = opts.carType;
  const cc = opts.countryCode
    ? opts.countryCode.trim().toUpperCase()
    : "";
  if (cc && COUNTRY_CODE_RE.test(cc)) state.country = cc;
  return state;
}

/**
 * Public crawlable target for browse actions. State lives in
 * `buildBrowseSearchState` and is applied client-side by SearchStateLink.
 */
export function buildBrowseHref(_opts?: {
  countryCode?: string | null;
  carType?: string | null;
}): string {
  return "/search";
}
